import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useTrainingVideoOptions } from 'lib/hooks/queries/wizard/useTrainingVideoOptions';

import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import BaseModal from 'components/shared/Modal/Modal';
import S3Uploader from 'components/admin/pages/GeneralUploader/components/FileS3Multipart';

import useVideoFormik from '../lib/useVideoFormik';
import useVideoUploader from '../lib/useVideoUploader';
import { CHUNK_SIZE, FILE_UPLOAD_STATUS } from '../../GeneralUploader/components/lib/constants';

const Modal = ({ item: video, isOpen, handleClose }) => {
  const [files, setFiles] = useState([]);
  const [uploadProgressList, setUploadProgressList] = useState([]);
  const queryClient = useQueryClient();
  const { uploading, uploadFiles } = useVideoUploader();

  // Managed upload targets (onboarding training videos) come from the wizard domain:
  // selecting one uploads to its file_key instead of a filename-derived key.
  // Fetched only when the modal is actually open — it is mounted with the page
  const { data: trainingOptionsResponse } = useTrainingVideoOptions({
    options: { enabled: isOpen }
  });
  const trainingVideoOptions = (trainingOptionsResponse?.data ?? []).map((option) => ({
    label: option.name,
    value: option.file_key
  }));

  const formik = useVideoFormik(video, async (values, formikHelpers) => {
    const uploaded = await handleUpload(files, values);
    if (!uploaded) return;
    await queryClient.invalidateQueries({ queryKey: ['videos'] });
    setFiles([]);
    formikHelpers.resetForm();
    handleClose();
  });

  const handleUpload = async (files, formData = {}) => {
    const progressInit = files.map((file) => ({
      filename: file.name,
      progress: 0,
      total: Math.ceil(file.size / CHUNK_SIZE),
      size: file.size,
      status: FILE_UPLOAD_STATUS.INITIATED
    }));

    setUploadProgressList(progressInit);
    return uploadFiles(
      files,
      { filename: files[0].name, contentType: files[0].type, ...formData },
      setUploadProgressList
    );
  };

  const footerActions = {
    primary: {
      text: video ? 'Update' : 'Upload video',
      onClick: formik.handleSubmit,
      isLoading: uploading,
      isDisabled: files.length === 0
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      title="Upload video"
      bodyClassName="space-y-4"
      handleClose={handleClose}
      footerActions={footerActions}>
      <S3Uploader
        formats=".mov,.mp4"
        files={files}
        setFiles={setFiles}
        maxFiles={1}
        onUpload={handleUpload}
        uploadProgressList={uploadProgressList}
      />
      <div>
        <Select
          isClearable
          label="Onboarding training video (optional)"
          inputId="trainingVideoSelect"
          placeholder="Not a training video"
          options={trainingVideoOptions}
          value={
            trainingVideoOptions.find((option) => option.value === formik.values.file_key) ?? null
          }
          onChange={(option) => {
            formik.setFieldValue('file_key', option?.value ?? '');
            // Only manage the title while it's empty or still an auto-filled training
            // name — never clobber a title the admin typed themselves
            const isAutoTitle =
              !formik.values.title ||
              trainingVideoOptions.some(({ label }) => label === formik.values.title);
            if (!isAutoTitle) return;
            formik.setFieldValue('title', option?.label ?? '');
          }}
        />
        <p className="pt-1 text-sm text-neutral-600">
          Selecting a training stores the file under that training&apos;s key — it replaces the
          current video and keeps staff watch progress. Leave it empty for videos unrelated to
          onboarding.
        </p>
      </div>
      <Input
        label="Title"
        name="title"
        value={formik.values.title}
        onChange={formik.handleChange}
        required
        error={formik.touched.title && formik.errors.title}
      />
      <Input
        label="Description"
        name="description"
        value={formik.values.description}
        onChange={formik.handleChange}
        required
        error={formik.touched.description && formik.errors.description}
      />
      <Input
        label="Thumbnail URL"
        name="thumbnail_url"
        value={formik.values.thumbnail_url}
        onChange={formik.handleChange}
        error={formik.touched.thumbnail_url && formik.errors.thumbnail_url}
      />
    </BaseModal>
  );
};

export default Modal;
